import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, CheckCircle, XCircle, Eye, Clock, User, MapPin, Briefcase, FileText, Shield, AlertCircle, ExternalLink, Percent, Filter, MessageSquare, Calendar, CheckSquare, Square, Minus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { sendApplicationDecisionEmail } from '../../../services/resendEmailService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import SelectUI from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';



// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    draft: 'bg-gray-100 text-gray-600',
    submitted: 'bg-blue-100 text-blue-700',
    under_review: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const labels = {
    draft: 'Brouillon',
    submitted: 'Soumise',
    under_review: 'En examen',
    approved: 'Approuvée',
    rejected: 'Rejetée',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map?.[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels?.[status] || status}
    </span>
  );
};

// ─── Category Labels ───────────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  architecte: 'Architecte', geometre: 'Géomètre', decorateur: 'Décorateur',
  demenageur: 'Déménageur', nettoyage: 'Nettoyage', electricien: 'Électricien',
  plombier: 'Plombier', menuisier: 'Menuisier', climatisation: 'Climatisation',
  securite: 'Sécurité', autre: 'Autre',
};

// ─── Bulk Feedback Modal ──────────────────────────────────────────────────────
const BulkActionModal = ({ action, selectedCount, onConfirm, onCancel, loading }) => {
  const [feedback, setFeedback] = useState('');
  const isApprove = action === 'approved';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className={cn('w-10 h-10 rounded-full flex items-center justify-center mb-3', isApprove ? 'bg-green-100' : 'bg-red-100')}>
            {isApprove
              ? <CheckCircle className="w-5 h-5 text-green-600" />
              : <XCircle className="w-5 h-5 text-red-600" />}
          </div>
          <h3 className="text-lg font-bold text-foreground">
            {isApprove ? 'Approuver en lot' : 'Rejeter en lot'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedCount} candidature{selectedCount > 1 ? 's' : ''} sélectionnée{selectedCount > 1 ? 's' : ''}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-muted-foreground" />
              Note de feedback
              {!isApprove && <span className="text-destructive">*</span>}
            </label>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e?.target?.value)}
              rows={4}
              placeholder={isApprove
                ? 'Message optionnel envoyé aux candidats approuvés...' :'Motif de rejet envoyé aux candidats (obligatoire)...'}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
            <p className="text-xs text-muted-foreground">Ce message sera visible par les candidats concernés.</p>
          </div>
          {!isApprove && !feedback?.trim() && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Un message de feedback est requis pour le rejet.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30 rounded-b-xl">
          <Button variant="outline" size="default" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              if (!isApprove && !feedback?.trim()) return;
              onConfirm(feedback);
            }}
            disabled={loading || (!isApprove && !feedback?.trim())}
            variant={isApprove ? 'success' : 'danger'}
            size="default"
            loading={loading}
            className="gap-1.5"
          >
            {!loading && (isApprove ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />)}
            {isApprove ? 'Approuver tout' : 'Rejeter tout'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Application Detail Modal ─────────────────────────────────────────────────
const ApplicationDetailModal = ({ application, onClose, onAction, actionLoading }) => {
  const [feedback, setFeedback] = useState(application?.feedbackMessage || '');
  const [adminNotes, setAdminNotes] = useState(application?.adminNotes || '');
  const [commissionRate, setCommissionRate] = useState(application?.commissionRate != null ? String(application?.commissionRate) : '');
  const [activeSection, setActiveSection] = useState('identity');

  const sections = [
    { id: 'identity', label: 'Identité' },
    { id: 'profile', label: 'Profil' },
    { id: 'category', label: 'Catégorie' },
    { id: 'review', label: 'Décision Admin' },
  ];

  const handleApprove = () => {
    const rate = commissionRate !== '' ? parseFloat(commissionRate) : null;
    if (rate !== null && (isNaN(rate) || rate < 0 || rate > 100)) {
      alert('Le taux de commission doit être compris entre 0 et 100.');
      return;
    }
    onAction(application?.id, 'approved', feedback, adminNotes, rate);
  };
  const handleReject = () => {
    if (!feedback?.trim()) {
      alert('Veuillez saisir un message de feedback avant de rejeter la candidature.');
      return;
    }
    onAction(application?.id, 'rejected', feedback, adminNotes, null);
  };
  const handleUnderReview = () => onAction(application?.id, 'under_review', feedback, adminNotes, null);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{application?.fullName}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge status={application?.applicationStatus} />
                <span className="text-sm text-muted-foreground">
                  {CATEGORY_LABELS?.[application?.category] || application?.category}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-border px-6">
          {sections?.map(s => (
            <button
              key={s?.id}
              onClick={() => setActiveSection(s?.id)}
              className={cn(
                'py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                activeSection === s?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {s?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === 'identity' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Nom complet" value={application?.fullName} />
                <InfoField label="Email" value={application?.email} />
                <InfoField label="Téléphone" value={application?.phone || '—'} />
                <InfoField label="WhatsApp" value={application?.whatsapp || '—'} />
                <InfoField label="Ville" value={application?.city || '—'} />
                <InfoField label="Commune" value={application?.commune || '—'} />
              </div>
              <InfoField label="Adresse" value={application?.address || '—'} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Pièce d'identité</p>
                  {application?.idDocumentUrl ? (
                    <a href={application?.idDocumentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 text-sm font-medium">
                      <ExternalLink className="w-4 h-4" />Voir le document
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">Non fourni</span>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Licence professionnelle</p>
                  {application?.professionalLicenseUrl ? (
                    <a href={application?.professionalLicenseUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:text-primary/80 text-sm font-medium">
                      <ExternalLink className="w-4 h-4" />Voir la licence
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">Non fournie</span>
                  )}
                </div>
              </div>
              {application?.licenseNumber && <InfoField label="Numéro de licence" value={application?.licenseNumber} />}
            </div>
          )}

          {activeSection === 'profile' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Années d'expérience" value={`${application?.yearsExperience || 0} ans`} />
                <InfoField label="Devise" value={application?.currency || 'XOF'} />
                {application?.hourlyRate && <InfoField label="Tarif horaire" value={`${application?.hourlyRate} ${application?.currency || 'XOF'}`} />}
                {application?.dailyRate && <InfoField label="Tarif journalier" value={`${application?.dailyRate} ${application?.currency || 'XOF'}`} />}
              </div>
              {application?.bio && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Biographie</p>
                  <p className="text-sm text-foreground bg-muted/40 rounded-lg p-3 leading-relaxed">{application?.bio}</p>
                </div>
              )}
              {application?.serviceZones?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Zones d'intervention</p>
                  <div className="flex flex-wrap gap-1.5">
                    {application?.serviceZones?.map((z, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{z}</span>)}
                  </div>
                </div>
              )}
              {application?.languages?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Langues</p>
                  <div className="flex flex-wrap gap-1.5">
                    {application?.languages?.map((l, i) => <span key={i} className="px-2 py-0.5 bg-muted text-foreground rounded text-xs">{l}</span>)}
                  </div>
                </div>
              )}
              {application?.availableDays?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Jours disponibles</p>
                  <div className="flex flex-wrap gap-1.5">
                    {application?.availableDays?.map((d, i) => <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">{d}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'category' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Catégorie" value={CATEGORY_LABELS?.[application?.category] || application?.category} />
                <InfoField label="Spécialité" value={application?.specialty || '—'} />
              </div>
              {application?.tags?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {application?.tags?.map((t, i) => <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{t}</span>)}
                  </div>
                </div>
              )}
              {application?.certifications && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Certifications</p>
                  {Array.isArray(application?.certifications) && application?.certifications?.length > 0 ? (
                    <div className="space-y-2">
                      {application?.certifications?.map((cert, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-muted/40 rounded-lg">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-foreground">
                            {typeof cert === 'object' ? cert?.name || cert?.title || JSON.stringify(cert) : cert}
                          </span>
                          {typeof cert === 'object' && cert?.url && (
                            <a href={cert?.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-primary hover:text-primary/80">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Aucune certification fournie</span>
                  )}
                </div>
              )}
              {(application?.websiteUrl || application?.linkedinUrl) && (
                <div className="grid grid-cols-2 gap-4">
                  {application?.websiteUrl && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Site web</p>
                      <a href={application?.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">{application?.websiteUrl}</a>
                    </div>
                  )}
                  {application?.linkedinUrl && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">LinkedIn</p>
                      <a href={application?.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">{application?.linkedinUrl}</a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeSection === 'review' && (
            <div className="space-y-5">
              {/* Commission Rate */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <label className="block text-sm font-semibold text-amber-800 mb-2">
                  <Percent className="w-4 h-4 inline mr-1.5 text-amber-600" />
                  Taux de commission (%)
                  <span className="ml-2 text-xs font-normal text-amber-600">Requis pour l'approbation</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={commissionRate}
                      onChange={e => setCommissionRate(e?.target?.value)}
                      placeholder="Ex: 10"
                      className="pr-10 border-amber-300 focus-visible:ring-amber-500 bg-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 font-semibold text-sm pointer-events-none">%</span>
                  </div>
                  <div className="flex gap-2">
                    {[5, 8, 10, 15]?.map(v => (
                      <Button
                        key={v}
                        type="button"
                        size="xs"
                        variant={commissionRate === String(v) ? 'warning' : 'outline'}
                        onClick={() => setCommissionRate(String(v))}
                        className={commissionRate === String(v) ? '' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
                      >
                        {v}%
                      </Button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-1.5">Ce taux sera appliqué sur les transactions du professionnel via la plateforme.</p>
                {application?.commissionRate != null && (
                  <p className="text-xs text-amber-700 mt-1 font-medium">Taux actuel : {application?.commissionRate}%</p>
                )}
              </div>

              {/* Admin Notes */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  Notes de vérification des antécédents
                </label>
                <textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e?.target?.value)}
                  rows={4}
                  placeholder="Résultats de la vérification des antécédents, documents contrôlés, observations internes..."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
                <p className="text-xs text-muted-foreground">Ces notes sont internes et non visibles par le candidat.</p>
              </div>

              {/* Feedback Message */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  Message de feedback au candidat
                  {application?.applicationStatus !== 'approved' && <span className="text-destructive">*</span>}
                </label>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e?.target?.value)}
                  rows={4}
                  placeholder="Message envoyé au candidat lors de l'approbation ou du rejet..."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
                <p className="text-xs text-muted-foreground">Ce message sera visible par le candidat.</p>
              </div>

              {application?.rejectionReason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs font-semibold text-red-700 mb-1">Motif de rejet précédent</p>
                  <p className="text-sm text-red-600">{application?.rejectionReason}</p>
                </div>
              )}
              {application?.reviewedAt && (
                <div className="p-3 bg-muted/40 rounded-lg text-xs text-muted-foreground">
                  Dernière révision : {new Date(application?.reviewedAt)?.toLocaleString('fr-FR')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30 rounded-b-xl">
          <div className="flex items-center gap-2">
            {application?.applicationStatus !== 'under_review' && (
              <Button
                onClick={handleUnderReview}
                disabled={actionLoading}
                variant="outline"
                size="default"
                className="gap-1.5 border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100"
              >
                <Clock className="w-4 h-4" />En examen
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="default" onClick={onClose}>
              Fermer
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              variant="danger"
              size="default"
              className="gap-1.5"
            >
              <XCircle className="w-4 h-4" />Rejeter
            </Button>
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              variant="success"
              size="default"
              className="gap-1.5"
            >
              <CheckCircle className="w-4 h-4" />Approuver & Publier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Info Field ───────────────────────────────────────────────────────────────
const InfoField = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
    <p className="text-sm text-foreground">{value || '—'}</p>
  </div>
);

// ─── Inline Note Cell ─────────────────────────────────────────────────────────
const InlineNoteCell = ({ appId, value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef?.current) inputRef?.current?.focus();
  }, [editing]);

  const handleSave = () => {
    onChange(appId, draft);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => setDraft(e?.target?.value)}
          onKeyDown={e => { if (e?.key === 'Enter') handleSave(); if (e?.key === 'Escape') setEditing(false); }}
          className="w-36 border border-ring rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring bg-background"
          placeholder="Note..."
        />
        <button onClick={handleSave} className="text-green-600 hover:text-green-700">
          <CheckCircle className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setEditing(false)} className="text-muted-foreground hover:text-foreground">
          <XCircle className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDraft(value || ''); setEditing(true); }}
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors group max-w-[140px]"
      title={value || 'Ajouter une note'}
    >
      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 group-hover:text-primary" />
      <span className="truncate">{value || <span className="italic">Note...</span>}</span>
    </button>
  );
};

// ─── Application Row ──────────────────────────────────────────────────────────
const ApplicationRow = ({ app, onView, selected, onToggleSelect, inlineNote, onNoteChange }) => {
  const submittedDate = app?.submittedAt
    ? new Date(app?.submittedAt)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <tr className={cn('hover:bg-muted/30 transition-colors', selected && 'bg-primary/5')}>
      <td className="px-3 py-3">
        <button
          onClick={() => onToggleSelect(app?.id)}
          className="text-muted-foreground hover:text-primary transition-colors"
        >
          {selected
            ? <CheckSquare className="w-4 h-4 text-primary" />
            : <Square className="w-4 h-4" />}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-blue-700 font-semibold text-xs">{app?.fullName?.charAt(0)?.toUpperCase()}</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{app?.fullName}</p>
            <p className="text-xs text-muted-foreground">{app?.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-foreground">{CATEGORY_LABELS?.[app?.category] || app?.category || '—'}</span>
        {app?.specialty && <p className="text-xs text-muted-foreground">{app?.specialty}</p>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
          {app?.commune || app?.city || '—'}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">{submittedDate}</span>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={app?.applicationStatus} />
      </td>
      <td className="px-4 py-3">
        <InlineNoteCell appId={app?.id} value={inlineNote} onChange={onNoteChange} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {app?.idDocumentUrl && (
            <span title="Pièce d'identité fournie" className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-3 h-3 text-green-600" />
            </span>
          )}
          {app?.professionalLicenseUrl && (
            <span title="Licence fournie" className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-3 h-3 text-blue-600" />
            </span>
          )}
          {app?.commissionRate != null && (
            <span title={`Commission: ${app?.commissionRate}%`} className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">
              {app?.commissionRate}%
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <Button
          onClick={() => onView(app)}
          variant="ghost"
          size="sm"
          className="gap-1.5 text-primary bg-primary/5 hover:bg-primary/10"
        >
          <Eye className="w-3.5 h-3.5" />Examiner
        </Button>
      </td>
    </tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ApplicationReviewPanel = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [stats, setStats] = useState({ submitted: 0, under_review: 0, approved: 0, rejected: 0 });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [inlineNotes, setInlineNotes] = useState({});

  const toCamel = (row) => ({
    id: row?.id,
    userId: row?.user_id,
    fullName: row?.full_name,
    email: row?.email,
    phone: row?.phone,
    whatsapp: row?.whatsapp,
    idDocumentUrl: row?.id_document_url,
    idDocumentType: row?.id_document_type,
    professionalLicenseUrl: row?.professional_license_url,
    licenseNumber: row?.license_number,
    yearsExperience: row?.years_experience,
    bio: row?.bio,
    city: row?.city,
    commune: row?.commune,
    address: row?.address,
    serviceZones: row?.service_zones || [],
    languages: row?.languages || [],
    websiteUrl: row?.website_url,
    linkedinUrl: row?.linkedin_url,
    hourlyRate: row?.hourly_rate,
    dailyRate: row?.daily_rate,
    currency: row?.currency,
    availableDays: row?.available_days || [],
    availableHoursStart: row?.available_hours_start,
    availableHoursEnd: row?.available_hours_end,
    category: row?.category,
    specialty: row?.specialty,
    tags: row?.tags || [],
    certifications: row?.certifications || [],
    applicationStatus: row?.application_status,
    rejectionReason: row?.rejection_reason,
    adminNotes: row?.admin_notes,
    feedbackMessage: row?.feedback_message,
    reviewedBy: row?.reviewed_by,
    reviewedAt: row?.reviewed_at,
    commissionRate: row?.commission_rate != null ? parseFloat(row?.commission_rate) : null,
    submittedAt: row?.submitted_at,
    createdAt: row?.created_at,
  });

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedIds(new Set());

      const statuses = ['submitted', 'under_review', 'approved', 'rejected'];
      const statCounts = {};
      await Promise.all(
        statuses?.map(async (s) => {
          const { count } = await supabase
            ?.from('professional_applications')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('application_status', s);
          statCounts[s] = count || 0;
        })
      );
      setStats(statCounts);

      let query = supabase
        ?.from('professional_applications')
        ?.select('*')
        ?.order('submitted_at', { ascending: false });

      if (statusFilter !== 'all') query = query?.eq('application_status', statusFilter);
      if (categoryFilter !== 'all') query = query?.eq('category', categoryFilter);
      if (dateFrom) query = query?.gte('submitted_at', dateFrom);
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate?.setDate(toDate?.getDate() + 1);
        query = query?.lt('submitted_at', toDate?.toISOString()?.split('T')?.[0]);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setApplications((data || [])?.map(toCamel));
    } catch (err) {
      console.error('fetchApplications error:', err?.message);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleAction = async (id, newStatus, feedbackMessage, adminNotes, commissionRate) => {
    try {
      setActionLoading(true);
      const { data: { user } } = await supabase?.auth?.getUser();

      const payload = {
        application_status: newStatus,
        reviewed_by: user?.id,
        reviewed_at: new Date()?.toISOString(),
        feedback_message: feedbackMessage || null,
        admin_notes: adminNotes || null,
      };
      if (commissionRate !== null && commissionRate !== undefined) payload.commission_rate = commissionRate;
      if (newStatus === 'rejected') payload.rejection_reason = feedbackMessage || null;

      const { error: updateError } = await supabase
        ?.from('professional_applications')
        ?.update(payload)
        ?.eq('id', id);
      if (updateError) throw updateError;

      if (newStatus === 'approved') {
        const app = applications?.find(a => a?.id === id);
        if (app) {
          await supabase?.from('professionals')?.insert({
            full_name: app?.fullName, email: app?.email, phone: app?.phone,
            whatsapp: app?.whatsapp, category: app?.category, specialty: app?.specialty,
            bio: app?.bio, years_experience: app?.yearsExperience, city: app?.city,
            commune: app?.commune, address: app?.address, service_zones: app?.serviceZones,
            languages: app?.languages, website_url: app?.websiteUrl, linkedin_url: app?.linkedinUrl,
            hourly_rate: app?.hourlyRate, daily_rate: app?.dailyRate, currency: app?.currency,
            available_days: app?.availableDays, available_hours_start: app?.availableHoursStart,
            available_hours_end: app?.availableHoursEnd, tags: app?.tags,
            status: 'active', is_verified: true,
            commission_rate: commissionRate !== null && commissionRate !== undefined ? commissionRate : null,
            created_by: user?.id,
          });
        }
      }

      if (newStatus === 'approved' || newStatus === 'rejected') {
        const app = applications?.find(a => a?.id === id);
        if (app?.email) {
          sendApplicationDecisionEmail({
            applicant_name: app?.fullName,
            applicant_email: app?.email,
            decision: newStatus,
            category: CATEGORY_LABELS?.[app?.category] || app?.category,
            feedback_message: feedbackMessage || null,
            commission_rate: newStatus === 'approved' ? commissionRate : null,
          })?.catch(err => console.warn('[ApplicationReview] Decision email error:', err?.message));
        }
      }

      const statusLabels = {
        approved: 'Candidature approuvée et profil publié !',
        rejected: 'Candidature rejetée.',
        under_review: 'Candidature marquée "En examen".',
      };
      setSuccessMsg(statusLabels?.[newStatus] || 'Statut mis à jour.');
      setTimeout(() => setSuccessMsg(''), 4000);
      setSelectedApp(null);
      fetchApplications();
    } catch (err) {
      console.error('handleAction error:', err?.message);
      alert('Erreur : ' + err?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkConfirm = async (feedbackMessage) => {
    try {
      setActionLoading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      const ids = Array.from(selectedIds);

      const payload = {
        application_status: bulkAction,
        reviewed_by: user?.id,
        reviewed_at: new Date()?.toISOString(),
        feedback_message: feedbackMessage || null,
      };
      if (bulkAction === 'rejected') payload.rejection_reason = feedbackMessage || null;

      const { error: updateError } = await supabase
        ?.from('professional_applications')
        ?.update(payload)
        ?.in('id', ids);
      if (updateError) throw updateError;

      if (bulkAction === 'approved') {
        const toApprove = applications?.filter(a => selectedIds?.has(a?.id));
        await Promise.all(toApprove?.map(app =>
          supabase?.from('professionals')?.insert({
            full_name: app?.fullName, email: app?.email, phone: app?.phone,
            whatsapp: app?.whatsapp, category: app?.category, specialty: app?.specialty,
            bio: app?.bio, years_experience: app?.yearsExperience, city: app?.city,
            commune: app?.commune, address: app?.address, service_zones: app?.serviceZones,
            languages: app?.languages, website_url: app?.websiteUrl, linkedin_url: app?.linkedinUrl,
            hourly_rate: app?.hourlyRate, daily_rate: app?.dailyRate, currency: app?.currency,
            available_days: app?.availableDays, available_hours_start: app?.availableHoursStart,
            available_hours_end: app?.availableHoursEnd, tags: app?.tags,
            status: 'active', is_verified: true, created_by: user?.id,
          })
        ));
      }

      if (bulkAction === 'approved' || bulkAction === 'rejected') {
        const toNotify = applications?.filter(a => selectedIds?.has(a?.id) && a?.email);
        toNotify?.forEach(app => {
          sendApplicationDecisionEmail({
            applicant_name: app?.fullName,
            applicant_email: app?.email,
            decision: bulkAction,
            category: CATEGORY_LABELS?.[app?.category] || app?.category,
            feedback_message: feedbackMessage || null,
            commission_rate: null,
          })?.catch(err => console.warn('[ApplicationReview] Bulk decision email error:', err?.message));
        });
      }

      setSuccessMsg(
        bulkAction === 'approved'
          ? `${ids?.length} candidature${ids?.length > 1 ? 's' : ''} approuvée${ids?.length > 1 ? 's' : ''} et publiée${ids?.length > 1 ? 's' : ''} !`
          : `${ids?.length} candidature${ids?.length > 1 ? 's' : ''} rejetée${ids?.length > 1 ? 's' : ''}.`
      );
      setTimeout(() => setSuccessMsg(''), 4000);
      setBulkAction(null);
      fetchApplications();
    } catch (err) {
      console.error('handleBulkConfirm error:', err?.message);
      alert('Erreur : ' + err?.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id); else next?.add(id);
      return next;
    });
  };

  const filtered = applications?.filter(a => {
    const q = search?.toLowerCase();
    return (
      a?.fullName?.toLowerCase()?.includes(q) ||
      a?.email?.toLowerCase()?.includes(q) ||
      a?.category?.toLowerCase()?.includes(q) ||
      a?.commune?.toLowerCase()?.includes(q)
    );
  });

  const allSelected = filtered?.length > 0 && selectedIds?.size === filtered?.length;
  const someSelected = selectedIds?.size > 0 && selectedIds?.size < filtered?.length;

  const handleSelectAll = () => {
    if (selectedIds?.size === filtered?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered?.map(a => a?.id)));
    }
  };

  const handleNoteChange = (appId, note) => {
    setInlineNotes(prev => ({ ...prev, [appId]: note }));
  };

  const clearFilters = () => {
    setCategoryFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const hasActiveFilters = categoryFilter !== 'all' || dateFrom || dateTo || search;

  // Options for Select components
  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'submitted', label: 'Soumises' },
    { value: 'under_review', label: 'En examen' },
    { value: 'approved', label: 'Approuvées' },
    { value: 'rejected', label: 'Rejetées' },
  ];

  const categoryOptions = [
    { value: 'all', label: 'Tous les types' },
    ...Object.entries(CATEGORY_LABELS)?.map(([key, label]) => ({ value: key, label })),
  ];

  const statCards = [
    { key: 'submitted', label: 'Soumises', color: 'blue', icon: Clock },
    { key: 'under_review', label: 'En examen', color: 'yellow', icon: Eye },
    { key: 'approved', label: 'Approuvées', color: 'green', icon: CheckCircle },
    { key: 'rejected', label: 'Rejetées', color: 'red', icon: XCircle },
  ];

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMsg && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-medium">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />{successMsg}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {statCards?.map(({ key, label, color, icon }) => {
          const CardIcon = icon;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={cn(
                'p-4 rounded-xl border-2 text-left transition-all',
                statusFilter === key
                  ? `border-${color}-400 bg-${color}-50`
                  : 'border-border bg-card hover:border-input'
              )}
            >
              <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center mb-2`}>
                <CardIcon className={`w-4 h-4 text-${color}-600`} />
              </div>
              <p className="text-2xl font-bold text-foreground">{stats?.[key] || 0}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters Bar */}
      <div className="bg-card rounded-xl border border-border p-4 space-y-3">
        {/* Row 1: Search + Status + Refresh */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email, catégorie..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="pl-9"
            />
          </div>
          <SelectUI
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-44"
          />
          <Button
            onClick={fetchApplications}
            variant="outline"
            size="default"
            className="gap-1.5"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            Actualiser
          </Button>
        </div>

        {/* Row 2: Category + Date range */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <SelectUI
              options={categoryOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e?.target?.value)}
              className="w-36"
              title="Date de début"
            />
            <span className="text-muted-foreground text-sm">→</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e?.target?.value)}
              className="w-36"
              title="Date de fin"
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-primary hover:text-primary/80 underline">
              Réinitialiser les filtres
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds?.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-xl">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckSquare className="w-4 h-4" />
            {selectedIds?.size} sélectionnée{selectedIds?.size > 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              onClick={() => setBulkAction('approved')}
              disabled={actionLoading}
              variant="success"
              size="default"
              className="gap-1.5"
            >
              <CheckCircle className="w-4 h-4" />
              Approuver la sélection
            </Button>
            <Button
              onClick={() => setBulkAction('rejected')}
              disabled={actionLoading}
              variant="danger"
              size="default"
              className="gap-1.5"
            >
              <XCircle className="w-4 h-4" />
              Rejeter la sélection
            </Button>
            <Button
              onClick={() => setSelectedIds(new Set())}
              variant="outline"
              size="default"
            >
              Désélectionner
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {error && (
          <div className="flex items-center gap-2 p-4 bg-destructive/10 border-b border-destructive/20 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Briefcase className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">Aucune candidature trouvée</p>
            <p className="text-xs mt-1">
              {hasActiveFilters ? 'Essayez de modifier vos filtres' : 'Aucune candidature enregistrée'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="px-3 py-3 text-left">
                    <button onClick={handleSelectAll} className="text-muted-foreground hover:text-primary transition-colors" title="Tout sélectionner">
                      {allSelected
                        ? <CheckSquare className="w-4 h-4 text-primary" />
                        : someSelected
                          ? <Minus className="w-4 h-4 text-primary/60" />
                          : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Candidat</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Catégorie</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Localisation</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Soumise le</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Note inline</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Documents</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(app => (
                  <ApplicationRow
                    key={app?.id}
                    app={app}
                    onView={setSelectedApp}
                    selected={selectedIds?.has(app?.id)}
                    onToggleSelect={handleToggleSelect}
                    inlineNote={inlineNotes?.[app?.id] || ''}
                    onNoteChange={handleNoteChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered?.length > 0 && (
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
            <span>{filtered?.length} candidature{filtered?.length > 1 ? 's' : ''} affichée{filtered?.length > 1 ? 's' : ''}</span>
            {selectedIds?.size > 0 && (
              <span className="text-primary font-medium">{selectedIds?.size} sélectionnée{selectedIds?.size > 1 ? 's' : ''}</span>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedApp && (
        <ApplicationDetailModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onAction={handleAction}
          actionLoading={actionLoading}
        />
      )}

      {/* Bulk Action Modal */}
      {bulkAction && (
        <BulkActionModal
          action={bulkAction}
          selectedCount={selectedIds?.size}
          onConfirm={handleBulkConfirm}
          onCancel={() => setBulkAction(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default ApplicationReviewPanel;
